/**
 * Category Manager Component.
 *
 * Recursive tree editor for nested product categories. Supports:
 * - Add categories at any level
 * - Edit existing category label, value, and code
 * - Delete categories (with confirmation for categories that have children)
 * - Nest subcategories under any parent
 * - Reorder categories (move up/down within siblings)
 * - Reparent categories (move to root or under a different parent)
 * - Indent/outdent (keyboard shortcut-style: move into sibling above or out to parent level)
 * - Unlimited depth
 *
 * Categories are stored as a tree in the org settings JSONB field.
 * Each node has: id, value, label, code, children[].
 */

import { useState, useCallback } from "react";
import {
  ChevronRight,
  ChevronDown,
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  FolderPlus,
  ArrowUp,
  ArrowDown,
  MoveRight,
  CornerDownRight,
  CornerLeftUp,
} from "lucide-react";
import type { CategoryOption } from "@/types/settings";

interface CategoryManagerProps {
  categories: CategoryOption[];
  onChange: (categories: CategoryOption[]) => void;
}

/** Generate a short unique ID for new categories. */
function genId(): string {
  return `cat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

/** Flatten a category tree into a list of {id, label, depth} for move target selection. */
function flattenTree(
  cats: CategoryOption[],
  depth = 0,
  excludeId?: string,
): Array<{ id: string; label: string; depth: number }> {
  const result: Array<{ id: string; label: string; depth: number }> = [];
  for (const cat of cats) {
    if (cat.id === excludeId) continue;
    result.push({ id: cat.id, label: cat.label, depth });
    if (cat.children) {
      result.push(...flattenTree(cat.children, depth + 1, excludeId));
    }
  }
  return result;
}

/** Remove a category by ID from the tree and return the removed node + new tree. */
function removeFromTree(
  tree: CategoryOption[],
  targetId: string,
): { remaining: CategoryOption[]; removed: CategoryOption | null } {
  for (let i = 0; i < tree.length; i++) {
    const item = tree[i]!;
    if (item.id === targetId) {
      return {
        remaining: [...tree.slice(0, i), ...tree.slice(i + 1)],
        removed: item,
      };
    }
    if (item.children && item.children.length > 0) {
      const result = removeFromTree(item.children, targetId);
      if (result.removed) {
        return {
          remaining: tree.map((c, j) =>
            j === i ? { ...c, children: result.remaining } : c,
          ),
          removed: result.removed,
        };
      }
    }
  }
  return { remaining: tree, removed: null };
}

/** Insert a category under a target parent ID (or root if parentId is null). */
function insertIntoTree(
  tree: CategoryOption[],
  node: CategoryOption,
  parentId: string | null,
): CategoryOption[] {
  if (!parentId) {
    return [...tree, node];
  }
  return tree.map((cat) => {
    if (cat.id === parentId) {
      return { ...cat, children: [...(cat.children || []), node] };
    }
    if (cat.children) {
      return {
        ...cat,
        children: insertIntoTree(cat.children, node, parentId),
      };
    }
    return cat;
  });
}

/** Find the parent ID of a category in the tree. Returns null if at root. */
function findParentId(
  tree: CategoryOption[],
  targetId: string,
  currentParent: string | null = null,
): string | null | undefined {
  for (const cat of tree) {
    if (cat.id === targetId) return currentParent;
    if (cat.children) {
      const found = findParentId(cat.children, targetId, cat.id);
      if (found !== undefined) return found;
    }
  }
  return undefined;
}

/** Find siblings array and index for a category. */
function findSiblings(
  tree: CategoryOption[],
  targetId: string,
): {
  siblings: CategoryOption[];
  index: number;
  parentId: string | null;
} | null {
  for (let i = 0; i < tree.length; i++) {
    if (tree[i]!.id === targetId) {
      return { siblings: tree, index: i, parentId: null };
    }
  }
  for (const cat of tree) {
    if (cat.children) {
      for (let i = 0; i < cat.children.length; i++) {
        if (cat.children[i]!.id === targetId) {
          return { siblings: cat.children, index: i, parentId: cat.id };
        }
      }
      const found = findSiblings(cat.children, targetId);
      if (found) return found;
    }
  }
  return null;
}

function CategoryManager({ categories, onChange }: CategoryManagerProps) {
  const [newCat, setNewCat] = useState({
    value: "",
    label: "",
    code: "",
  });

  const addTopLevel = () => {
    if (!newCat.value || !newCat.label || !newCat.code) return;
    onChange([...categories, { id: genId(), ...newCat, children: [] }]);
    setNewCat({ value: "", label: "", code: "" });
  };

  /** Move a category up or down within its sibling list. */
  const handleReorder = useCallback(
    (catId: string, direction: "up" | "down") => {
      const deepClone = JSON.parse(
        JSON.stringify(categories),
      ) as CategoryOption[];

      function reorderInList(list: CategoryOption[]): boolean {
        const idx = list.findIndex((c) => c.id === catId);
        if (idx >= 0) {
          const newIdx = direction === "up" ? idx - 1 : idx + 1;
          if (newIdx < 0 || newIdx >= list.length) return false;
          [list[idx], list[newIdx]] = [list[newIdx]!, list[idx]!];
          return true;
        }
        for (const cat of list) {
          if (cat.children && reorderInList(cat.children)) return true;
        }
        return false;
      }

      if (reorderInList(deepClone)) {
        onChange(deepClone);
      }
    },
    [categories, onChange],
  );

  /** Move a category to a different parent (or to root). */
  const handleMove = useCallback(
    (catId: string, newParentId: string | null) => {
      const { remaining, removed } = removeFromTree(categories, catId);
      if (!removed) return;
      const updated = insertIntoTree(remaining, removed, newParentId);
      onChange(updated);
    },
    [categories, onChange],
  );

  /** Indent: move category to become a child of the sibling above it. */
  const handleIndent = useCallback(
    (catId: string) => {
      const info = findSiblings(categories, catId);
      if (!info || info.index === 0) return;
      const siblingAbove = info.siblings[info.index - 1]!;
      handleMove(catId, siblingAbove.id);
    },
    [categories, handleMove],
  );

  /** Outdent: move category out of its parent to the grandparent level. */
  const handleOutdent = useCallback(
    (catId: string) => {
      const parentId = findParentId(categories, catId);
      if (parentId === undefined || parentId === null) return; // Already at root
      const grandparentId = findParentId(categories, parentId);
      handleMove(catId, grandparentId ?? null);
    },
    [categories, handleMove],
  );

  return (
    <div className="space-y-1">
      {categories.map((cat, i) => (
        <CategoryNode
          key={cat.id}
          category={cat}
          depth={0}
          index={i}
          siblingCount={categories.length}
          allCategories={categories}
          onUpdate={(updated) => {
            onChange(categories.map((c) => (c.id === cat.id ? updated : c)));
          }}
          onDelete={() => {
            onChange(categories.filter((c) => c.id !== cat.id));
          }}
          onReorder={handleReorder}
          onMove={handleMove}
          onIndent={handleIndent}
          onOutdent={handleOutdent}
        />
      ))}

      {/* Add Top-Level Category */}
      <div className="mt-3 flex gap-2">
        <input
          placeholder="Value (e.g., trailer)"
          value={newCat.value}
          onChange={(e) =>
            setNewCat({
              ...newCat,
              value: e.target.value.toLowerCase().replace(/\s+/g, "_"),
            })
          }
          className="flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-sm"
        />
        <input
          placeholder="Label"
          value={newCat.label}
          onChange={(e) => setNewCat({ ...newCat, label: e.target.value })}
          className="flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-sm"
        />
        <input
          placeholder="Code"
          value={newCat.code}
          onChange={(e) =>
            setNewCat({ ...newCat, code: e.target.value.toUpperCase() })
          }
          className="w-20 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-sm font-mono"
          maxLength={4}
        />
        <button
          onClick={addTopLevel}
          disabled={!newCat.value || !newCat.label || !newCat.code}
          className="flex items-center gap-1 rounded-[var(--radius-md)] bg-[var(--color-interactive-primary)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[var(--color-interactive-primary-hover)] disabled:opacity-40"
        >
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recursive Node
// ---------------------------------------------------------------------------

interface CategoryNodeProps {
  category: CategoryOption;
  depth: number;
  index: number;
  siblingCount: number;
  allCategories: CategoryOption[];
  onUpdate: (updated: CategoryOption) => void;
  onDelete: () => void;
  onReorder: (catId: string, direction: "up" | "down") => void;
  onMove: (catId: string, newParentId: string | null) => void;
  onIndent: (catId: string) => void;
  onOutdent: (catId: string) => void;
}

function CategoryNode({
  category,
  depth,
  index,
  siblingCount,
  allCategories,
  onUpdate,
  onDelete,
  onReorder,
  onMove,
  onIndent,
  onOutdent,
}: CategoryNodeProps) {
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [addingChild, setAddingChild] = useState(false);
  const [showMoveMenu, setShowMoveMenu] = useState(false);

  // Edit state
  const [editLabel, setEditLabel] = useState(category.label);
  const [editValue, setEditValue] = useState(category.value);
  const [editPrefix, setEditPrefix] = useState(category.code);

  // New child state
  const [newChild, setNewChild] = useState({
    value: "",
    label: "",
    code: "",
  });

  const hasChildren = category.children && category.children.length > 0;
  const canMoveUp = index > 0;
  const canMoveDown = index < siblingCount - 1;
  const canIndent = index > 0; // Can become child of sibling above
  const canOutdent = depth > 0; // Can move out of parent

  const handleSaveEdit = () => {
    onUpdate({
      ...category,
      label: editLabel,
      value: editValue,
      code: editPrefix,
    });
    setEditing(false);
  };

  const handleCancelEdit = () => {
    setEditLabel(category.label);
    setEditValue(category.value);
    setEditPrefix(category.code);
    setEditing(false);
  };

  const handleDelete = () => {
    if (hasChildren) {
      if (
        !confirm(
          `"${category.label}" has ${category.children!.length} subcategories. Delete all?`,
        )
      )
        return;
    }
    onDelete();
  };

  const handleAddChild = () => {
    if (!newChild.value || !newChild.label || !newChild.code) return;
    const child: CategoryOption = { id: genId(), ...newChild, children: [] };
    onUpdate({ ...category, children: [...(category.children || []), child] });
    setNewChild({ value: "", label: "", code: "" });
    setAddingChild(false);
    setExpanded(true);
  };

  const updateChild = (childId: string, updated: CategoryOption) => {
    onUpdate({
      ...category,
      children: (category.children || []).map((c) =>
        c.id === childId ? updated : c,
      ),
    });
  };

  const deleteChild = (childId: string) => {
    onUpdate({
      ...category,
      children: (category.children || []).filter((c) => c.id !== childId),
    });
  };

  // Targets for "Move to..." dropdown — all categories except self and descendants.
  const moveTargets = flattenTree(allCategories, 0, category.id);

  return (
    <div>
      <div
        className="group flex items-center gap-1 rounded-[var(--radius-md)] px-2 py-1.5 hover:bg-[var(--surface-sunken)]"
        style={{ paddingLeft: `${depth * 24 + 8}px` }}
      >
        {/* Expand/Collapse Toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex h-5 w-5 items-center justify-center text-[var(--text-tertiary)]"
        >
          {hasChildren ? (
            expanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )
          ) : (
            <span className="h-4 w-4" />
          )}
        </button>

        {editing ? (
          /* Edit Mode */
          <div className="flex flex-1 items-center gap-2">
            <input
              value={editValue}
              onChange={(e) =>
                setEditValue(e.target.value.toLowerCase().replace(/\s+/g, "_"))
              }
              className="w-28 rounded border border-blue-300 px-2 py-0.5 text-sm"
            />
            <input
              value={editLabel}
              onChange={(e) => setEditLabel(e.target.value)}
              className="flex-1 rounded border border-blue-300 px-2 py-0.5 text-sm"
              autoFocus
            />
            <input
              value={editPrefix}
              onChange={(e) => setEditPrefix(e.target.value.toUpperCase())}
              className="w-16 rounded border border-blue-300 px-2 py-0.5 text-sm font-mono"
              maxLength={4}
            />
            <button
              onClick={handleSaveEdit}
              className="text-[var(--text-success)] hover:text-green-800"
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              onClick={handleCancelEdit}
              className="text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          /* Display Mode */
          <>
            <span className="flex-1 text-sm font-medium text-[var(--text-primary)]">
              {category.label}
            </span>
            <span className="text-xs text-[var(--text-tertiary)]">
              {category.value}
            </span>
            <span className="ml-2 rounded bg-[var(--surface-sunken)] px-1.5 py-0.5 font-mono text-xs text-[var(--text-secondary)]">
              {category.code}
            </span>

            {/* Action Buttons (visible on hover) */}
            <div className="ml-2 flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
              {/* Reorder */}
              <button
                onClick={() => onReorder(category.id, "up")}
                disabled={!canMoveUp}
                title="Move up"
                className="p-0.5 text-[var(--text-tertiary)] hover:text-[var(--text-primary)] disabled:opacity-20"
              >
                <ArrowUp className="h-3 w-3" />
              </button>
              <button
                onClick={() => onReorder(category.id, "down")}
                disabled={!canMoveDown}
                title="Move down"
                className="p-0.5 text-[var(--text-tertiary)] hover:text-[var(--text-primary)] disabled:opacity-20"
              >
                <ArrowDown className="h-3 w-3" />
              </button>

              {/* Indent / Outdent */}
              {canIndent && (
                <button
                  onClick={() => onIndent(category.id)}
                  title="Indent (make subcategory of item above)"
                  className="p-0.5 text-[var(--text-tertiary)] hover:text-[var(--color-interactive-primary)]"
                >
                  <CornerDownRight className="h-3 w-3" />
                </button>
              )}
              {canOutdent && (
                <button
                  onClick={() => onOutdent(category.id)}
                  title="Outdent (move up one level)"
                  className="p-0.5 text-[var(--text-tertiary)] hover:text-[var(--color-interactive-primary)]"
                >
                  <CornerLeftUp className="h-3 w-3" />
                </button>
              )}

              <div className="w-px h-3 bg-[var(--border-subtle)] mx-0.5" />

              {/* Move to specific parent */}
              <div className="relative">
                <button
                  onClick={() => setShowMoveMenu(!showMoveMenu)}
                  title="Move to..."
                  className="p-0.5 text-[var(--text-tertiary)] hover:text-[var(--color-interactive-primary)]"
                >
                  <MoveRight className="h-3 w-3" />
                </button>
                {showMoveMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowMoveMenu(false)}
                    />
                    <div className="absolute right-0 top-6 z-20 w-52 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] shadow-lg py-1 max-h-48 overflow-y-auto">
                      <button
                        onClick={() => {
                          onMove(category.id, null);
                          setShowMoveMenu(false);
                        }}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--surface-sunken)] font-medium text-[var(--text-primary)]"
                      >
                        Root level
                      </button>
                      {moveTargets.map((t) => (
                        <button
                          key={t.id}
                          onClick={() => {
                            onMove(category.id, t.id);
                            setShowMoveMenu(false);
                          }}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--surface-sunken)] text-[var(--text-secondary)]"
                          style={{ paddingLeft: `${t.depth * 12 + 12}px` }}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div className="w-px h-3 bg-[var(--border-subtle)] mx-0.5" />

              <button
                onClick={() => setAddingChild(!addingChild)}
                title="Add subcategory"
                className="p-0.5 text-[var(--text-tertiary)] hover:text-[var(--color-interactive-primary)]"
              >
                <FolderPlus className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setEditing(true)}
                title="Edit"
                className="p-0.5 text-[var(--text-tertiary)] hover:text-[var(--color-interactive-primary)]"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={handleDelete}
                title="Delete"
                className="p-0.5 text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Add Child Form */}
      {addingChild && (
        <div
          className="flex gap-2 py-1"
          style={{ paddingLeft: `${(depth + 1) * 24 + 16}px` }}
        >
          <input
            placeholder="Value"
            value={newChild.value}
            onChange={(e) =>
              setNewChild({
                ...newChild,
                value: e.target.value.toLowerCase().replace(/\s+/g, "_"),
              })
            }
            className="flex-1 rounded border border-blue-300 px-2 py-1 text-sm"
            autoFocus
          />
          <input
            placeholder="Label"
            value={newChild.label}
            onChange={(e) =>
              setNewChild({ ...newChild, label: e.target.value })
            }
            className="flex-1 rounded border border-blue-300 px-2 py-1 text-sm"
          />
          <input
            placeholder="Code"
            value={newChild.code}
            onChange={(e) =>
              setNewChild({
                ...newChild,
                code: e.target.value.toUpperCase(),
              })
            }
            className="w-16 rounded border border-blue-300 px-2 py-1 text-sm font-mono"
            maxLength={4}
          />
          <button
            onClick={handleAddChild}
            disabled={!newChild.value || !newChild.label || !newChild.code}
            className="text-[var(--text-success)] hover:text-green-800 disabled:opacity-40"
          >
            <Check className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              setAddingChild(false);
              setNewChild({ value: "", label: "", code: "" });
            }}
            className="text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Children (recursive) */}
      {expanded && hasChildren && (
        <div>
          {category.children!.map((child, i) => (
            <CategoryNode
              key={child.id}
              category={child}
              depth={depth + 1}
              index={i}
              siblingCount={category.children!.length}
              allCategories={allCategories}
              onUpdate={(updated) => updateChild(child.id, updated)}
              onDelete={() => deleteChild(child.id)}
              onReorder={onReorder}
              onMove={onMove}
              onIndent={onIndent}
              onOutdent={onOutdent}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default CategoryManager;
